import { useEffect, useRef, useState } from 'react';
import { arcadeAudio } from '../../platform/audio';
import { fitCanvas, loadImages } from '../../platform/canvas';
import type { GameProps } from '../../platform/types';
import './hexbound.css';

const W = 1100;
const H = 680;
const SIZE = 28;
const COLS = 13;
const ROWS = 11;

type Kind = 'arrow' | 'frost' | 'beam' | 'mortar' | 'gold';
interface Hex { q: number; r: number; blocked: boolean; path: boolean }
interface Tower { q: number; r: number; kind: Kind; rank: number; cd: number }
interface Creep { x: number; y: number; hp: number; max: number; spd: number; i: number; worth: number; boss?: boolean }
interface Shot { x: number; y: number; tx: number; ty: number; dmg: number; c: string }

const COST: Record<Kind, number> = { arrow: 40, frost: 55, beam: 80, mortar: 90, gold: 50 };
const INFO: Record<Kind, string> = {
  arrow: 'Fast single target',
  frost: 'Slows the lane',
  beam: 'Line burn',
  mortar: 'Splash at range',
  gold: 'Pays every wave',
};

function axialToPixel(q: number, r: number) {
  const x = SIZE * Math.sqrt(3) * (q + r / 2) + 80;
  const y = SIZE * 1.5 * r + 70;
  return { x, y };
}
function key(q: number, r: number) { return `${q},${r}`; }

function makePath(map: number): Array<{ q: number; r: number }> {
  if (map === 1) {
    const p: Array<{ q: number; r: number }> = [];
    for (let q = 0; q < 12; q++) p.push({ q, r: 2 });
    for (let r = 2; r <= 8; r++) p.push({ q: 11, r });
    for (let q = 11; q >= 0; q--) p.push({ q, r: 8 });
    return p;
  }
  if (map === 2) {
    const p: Array<{ q: number; r: number }> = [];
    for (let r = 0; r < 10; r++) p.push({ q: 2, r });
    for (let q = 2; q < 10; q++) p.push({ q, r: 9 });
    for (let r = 9; r >= 1; r--) p.push({ q: 9, r });
    for (let q = 9; q < 13; q++) p.push({ q, r: 1 });
    return p;
  }
  const p: Array<{ q: number; r: number }> = [];
  for (let q = 0; q < 6; q++) p.push({ q, r: 1 });
  for (let r = 1; r < 5; r++) p.push({ q: 5, r });
  for (let q = 5; q < 11; q++) p.push({ q, r: 5 });
  for (let r = 5; r < 10; r++) p.push({ q: 10, r });
  return p;
}

export function HexboundGame({ onExit, onResult }: GameProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [sel, setSel] = useState<Kind>('arrow');
  const [over, setOver] = useState<{ score: number; won: boolean } | null>(null);
  const hud = useRef({ gold: 160, lives: 20, wave: 0, map: 0 });
  const selRef = useRef<Kind>('arrow');
  selRef.current = sel;

  useEffect(() => {
    const canvas = ref.current!;
    const ctx = fitCanvas(canvas, W, H)!;
    let dead = false;
    void arcadeAudio.playMusic('hex', '/assets/music/Mission-Plausible.ogg');
    let imgs = new Map<string, HTMLImageElement>();
    void loadImages({
      t1: '/assets/td/towerDefense_020_0.png',
      t2: '/assets/td/towerDefense_030_0.png',
      t3: '/assets/td/towerDefense_040_0.png',
      t4: '/assets/td/towerDefense_050_0.png',
      c: '/assets/td/towerDefense_080_0.png',
    }).then((m) => { imgs = m; });

    let map = 0;
    let path = makePath(0);
    const pathSet = new Set(path.map((p) => key(p.q, p.r)));
    const hexes: Hex[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let q = 0; q < COLS; q++) {
        hexes.push({ q, r, blocked: false, path: pathSet.has(key(q, r)) });
      }
    }
    const towers: Tower[] = [];
    const creeps: Creep[] = [];
    const shots: Shot[] = [];
    let gold = 160;
    let lives = 20;
    let wave = 0;
    let sending = false;
    let spawnLeft = 0;
    let spawnAcc = 0;
    let score = 0;
    let hover: { q: number; r: number } | null = null;
    let ended = false;

    const pix = path.map((p) => axialToPixel(p.q, p.r));

    const startWave = () => {
      if (sending || ended) return;
      wave += 1;
      sending = true;
      spawnLeft = 8 + wave * 2 + (wave % 5 === 0 ? 1 : 0);
      spawnAcc = 0;
      arcadeAudio.blip(300, 0.08);
    };

    const spawn = () => {
      const boss = wave % 5 === 0 && spawnLeft === 1;
      const hp = (18 + wave * 10) * (boss ? 8 : 1);
      creeps.push({ x: pix[0].x, y: pix[0].y, hp, max: hp, spd: boss ? 28 : 46 + wave, i: 0, worth: boss ? 40 : 6 + wave, boss });
    };

    const at = (q: number, r: number) => hexes.find((h) => h.q === q && h.r === r);

    const place = (q: number, r: number) => {
      const h = at(q, r);
      if (!h || h.path || h.blocked) return;
      const kind = selRef.current;
      if (gold < COST[kind]) return;
      const exist = towers.find((t) => t.q === q && t.r === r);
      if (exist) {
        if (exist.kind === kind && exist.rank < 3 && gold >= COST[kind]) {
          gold -= COST[kind];
          exist.rank += 1;
          arcadeAudio.blip(700, 0.06);
        }
        return;
      }
      gold -= COST[kind];
      h.blocked = true;
      towers.push({ q, r, kind, rank: 1, cd: 0 });
      arcadeAudio.blip(480, 0.05);
    };

    const onClick = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * W;
      const y = ((e.clientY - r.top) / r.height) * H;
      let best: Hex | null = null;
      let d = 1e9;
      for (const h of hexes) {
        const p = axialToPixel(h.q, h.r);
        const dd = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (dd < d) { d = dd; best = h; }
      }
      if (best && d < 30 * 30) place(best.q, best.r);
    };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * W;
      const y = ((e.clientY - r.top) / r.height) * H;
      let best = hexes[0];
      let d = 1e9;
      for (const h of hexes) {
        const p = axialToPixel(h.q, h.r);
        const dd = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (dd < d) { d = dd; best = h; }
      }
      hover = best;
    };
    canvas.addEventListener('pointerdown', onClick);
    canvas.addEventListener('pointermove', onMove);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ') { e.preventDefault(); startWave(); }
      if (e.key === '1') setSel('arrow');
      if (e.key === '2') setSel('frost');
      if (e.key === '3') setSel('beam');
      if (e.key === '4') setSel('mortar');
      if (e.key === '5') setSel('gold');
    };
    window.addEventListener('keydown', onKey);

    let last = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      if (dead) return;
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      if (!ended) tick(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };

    const tick = (dt: number) => {
      if (sending) {
        spawnAcc += dt;
        if (spawnLeft > 0 && spawnAcc > 0.55) {
          spawnAcc = 0;
          spawnLeft -= 1;
          spawn();
        }
        if (spawnLeft <= 0 && creeps.length === 0) {
          sending = false;
          gold += 25 + towers.filter((t) => t.kind === 'gold').reduce((n, t) => n + 8 * t.rank, 0);
          if (wave >= 15) {
            if (map >= 2) {
              ended = true;
              onResult({ score: score + lives * 50, won: true, stamps: { clear: true, skill: lives >= 15, mastery: lives === 20 } });
              setOver({ score: score + lives * 50, won: true });
            } else {
              map += 1;
              wave = 0;
              path = makePath(map);
              pathSet.clear();
              path.forEach((p) => pathSet.add(key(p.q, p.r)));
              hexes.forEach((h) => { h.path = pathSet.has(key(h.q, h.r)); if (h.path) h.blocked = false; });
              for (let i = towers.length - 1; i >= 0; i--) {
                if (pathSet.has(key(towers[i].q, towers[i].r))) {
                  const t = towers[i];
                  const cell = at(t.q, t.r);
                  if (cell) cell.blocked = false;
                  towers.splice(i, 1);
                }
              }
              pix.length = 0;
              pix.push(...path.map((p) => axialToPixel(p.q, p.r)));
              gold += 80;
            }
          }
        }
      }
      for (const c of creeps) {
        const next = pix[c.i + 1];
        if (!next) continue;
        const dx = next.x - c.x; const dy = next.y - c.y;
        const d = Math.hypot(dx, dy) || 1;
        const slow = towers.some((t) => {
          if (t.kind !== 'frost') return false;
          const p = axialToPixel(t.q, t.r);
          return (p.x - c.x) ** 2 + (p.y - c.y) ** 2 < (90 + t.rank * 10) ** 2;
        });
        const spd = c.spd * (slow ? 0.55 : 1);
        c.x += (dx / d) * spd * dt;
        c.y += (dy / d) * spd * dt;
        if (d < 6) c.i += 1;
      }
      for (let i = creeps.length - 1; i >= 0; i--) {
        if (creeps[i].i >= pix.length - 1) {
          lives -= creeps[i].boss ? 5 : 1;
          creeps.splice(i, 1);
          if (lives <= 0) {
            ended = true;
            onResult({ score, won: false, stamps: { clear: false } });
            setOver({ score, won: false });
          }
        }
      }
      for (const t of towers) {
        t.cd -= dt;
        const p = axialToPixel(t.q, t.r);
        const range = t.kind === 'mortar' ? 160 : t.kind === 'beam' ? 200 : 110 + t.rank * 12;
        let target: Creep | null = null;
        let best = 1e9;
        for (const c of creeps) {
          const d = (c.x - p.x) ** 2 + (c.y - p.y) ** 2;
          if (d < range * range && d < best) { best = d; target = c; }
        }
        if (target && t.cd <= 0 && t.kind !== 'gold') {
          t.cd = t.kind === 'arrow' ? 0.28 / t.rank : t.kind === 'beam' ? 0.12 : 0.9;
          const dmg = t.kind === 'arrow' ? 8 + t.rank * 4 : t.kind === 'beam' ? 3 + t.rank * 2 : 16 + t.rank * 8;
          shots.push({ x: p.x, y: p.y, tx: target.x, ty: target.y, dmg, c: t.kind === 'frost' ? '#7c5cff' : t.kind === 'mortar' ? '#ff5d8f' : '#ffb020' });
          if (t.kind === 'mortar') {
            for (const c of creeps) {
              if ((c.x - target.x) ** 2 + (c.y - target.y) ** 2 < 50 * 50) c.hp -= dmg * 0.5;
            }
          }
        }
      }
      for (let i = shots.length - 1; i >= 0; i--) {
        const s = shots[i];
        const dx = s.tx - s.x; const dy = s.ty - s.y;
        const d = Math.hypot(dx, dy) || 1;
        s.x += (dx / d) * 420 * dt;
        s.y += (dy / d) * 420 * dt;
        if (d < 10) {
          for (const c of creeps) {
            if ((c.x - s.tx) ** 2 + (c.y - s.ty) ** 2 < 16 * 16) c.hp -= s.dmg;
          }
          shots.splice(i, 1);
        }
      }
      for (let i = creeps.length - 1; i >= 0; i--) {
        if (creeps[i].hp <= 0) {
          gold += creeps[i].worth;
          score += creeps[i].worth * 10;
          creeps.splice(i, 1);
        }
      }
      hud.current = { gold, lives, wave, map };
    };

    const hexPath = (x: number, y: number) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = Math.PI / 180 * (60 * i - 30);
        const px = x + SIZE * Math.cos(a);
        const py = y + SIZE * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    const draw = () => {
      ctx.fillStyle = '#10141c';
      ctx.fillRect(0, 0, W, H);
      for (const h of hexes) {
        const p = axialToPixel(h.q, h.r);
        hexPath(p.x, p.y);
        ctx.fillStyle = h.path ? '#3a2a18' : h.blocked ? '#1c2430' : '#182028';
        ctx.fill();
        ctx.strokeStyle = '#2a3540';
        ctx.stroke();
      }
      if (hover) {
        const p = axialToPixel(hover.q, hover.r);
        hexPath(p.x, p.y);
        ctx.strokeStyle = '#ffb020';
        ctx.stroke();
      }
      for (const t of towers) {
        const p = axialToPixel(t.q, t.r);
        const im = imgs.get(t.kind === 'arrow' ? 't1' : t.kind === 'frost' ? 't2' : t.kind === 'beam' ? 't3' : 't4');
        if (im) ctx.drawImage(im, p.x - 22, p.y - 28, 44, 52);
        else {
          ctx.fillStyle = '#ffb020';
          ctx.beginPath(); ctx.arc(p.x, p.y, 10 + t.rank * 2, 0, 7); ctx.fill();
        }
        ctx.fillStyle = '#fff';
        ctx.font = '10px Outfit, sans-serif';
        ctx.fillText(String(t.rank), p.x - 3, p.y + 22);
      }
      for (const c of creeps) {
        const im = imgs.get('c');
        if (im) ctx.drawImage(im, c.x - 16, c.y - 16, 32, 32);
        else { ctx.fillStyle = c.boss ? '#ffb020' : '#ff5d8f'; ctx.beginPath(); ctx.arc(c.x, c.y, c.boss ? 14 : 9, 0, 7); ctx.fill(); }
        ctx.fillStyle = '#0008';
        ctx.fillRect(c.x - 12, c.y - 20, 24, 3);
        ctx.fillStyle = '#3ee0c5';
        ctx.fillRect(c.x - 12, c.y - 20, 24 * (c.hp / c.max), 3);
      }
      for (const s of shots) {
        ctx.fillStyle = s.c;
        ctx.fillRect(s.x - 2, s.y - 2, 4, 4);
      }
      ctx.fillStyle = '#fff6e8';
      ctx.font = '15px Outfit, sans-serif';
      ctx.fillText(`Gold ${gold}   Lives ${lives}   Wave ${wave}/15   Map ${map + 1}/3`, 20, 28);
      ctx.fillStyle = '#b7a994';
      ctx.font = '12px Outfit, sans-serif';
      ctx.fillText('Space start wave · click hex to build / merge same type · 1-5 select', 20, H - 16);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      canvas.removeEventListener('pointerdown', onClick);
      canvas.removeEventListener('pointermove', onMove);
      window.removeEventListener('keydown', onKey);
      arcadeAudio.stopMusic();
    };
  }, [onResult]);

  return (
    <div className="game-root hexbound">
      <canvas ref={ref} width={W} height={H} />
      <div className="hex-dock">
        {(Object.keys(COST) as Kind[]).map((k) => (
          <button key={k} type="button" className={sel === k ? 'play' : 'ghost'} onClick={() => setSel(k)}>
            {k} ${COST[k]}
            <small>{INFO[k]}</small>
          </button>
        ))}
        <button type="button" className="play" onClick={() => {
          window.dispatchEvent(new KeyboardEvent('keydown', { key: ' ' }));
        }}>Start wave</button>
      </div>
      {over && (
        <div className="overlay">
          <div className="panel">
            <h2>{over.won ? 'The hold stands' : 'The line broke'}</h2>
            <p>Score {over.score.toLocaleString()}</p>
            <button type="button" className="play" onClick={onExit}>Lobby</button>
          </div>
        </div>
      )}
    </div>
  );
}
